import {
  useEffect,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import {
  ActionIcon,
  Button as MantineButton,
  Drawer,
  InputWrapper,
  MantineProvider,
  Modal,
  UnstyledButton,
  createTheme,
  type MantineColorScheme,
} from "@mantine/core";
import { useMediaQuery, useReducedMotion } from "@mantine/hooks";

const theme = createTheme({
  primaryColor: "telegram",
  primaryShade: { light: 6, dark: 4 },
  colors: {
    telegram: [
      "#e8f6ff",
      "#d3edff",
      "#a6daff",
      "#72c5f7",
      "#43b0eb",
      "#239bd9",
      "#1488c4",
      "#0a74ac",
      "#0a638f",
      "#0a5275",
    ],
    dark: [
      "#e0e8f1",
      "#bac8d8",
      "#93a5bb",
      "#7489a1",
      "#50667f",
      "#384e66",
      "#293c52",
      "#203044",
      "#172638",
      "#111e2d",
    ],
  },
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", sans-serif',
  headings: { fontFamily: "inherit", fontWeight: "650" },
  defaultRadius: "md",
  radius: { xs: "4px", sm: "7px", md: "10px", lg: "14px", xl: "20px" },
  fontSizes: { xs: "12px", sm: "14px", md: "15px", lg: "17px", xl: "20px" },
  components: {
    InputWrapper: InputWrapper.extend({
      defaultProps: {
        inputWrapperOrder: ["label", "input", "description", "error"],
      },
    }),
    Button: MantineButton.extend({ defaultProps: { size: "md", fw: 600 } }),
    ActionIcon: ActionIcon.extend({
      defaultProps: { size: "lg", variant: "subtle" },
    }),
  },
});

function appearance(): "light" | "dark" {
  const telegram = window.Telegram?.WebApp;
  return telegram?.initData
    ? telegram.colorScheme === "dark"
      ? "dark"
      : "light"
    : window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
}
export function UiProvider({ children }: { children: ReactNode }) {
  const [scheme, setScheme] =
    useState<Exclude<MantineColorScheme, "auto">>(appearance);
  useEffect(() => {
    const update = () =>
      setScheme(
        document.documentElement.dataset.theme === "dark" ? "dark" : "light",
      );
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    update();
    return () => observer.disconnect();
  }, []);
  return (
    <MantineProvider theme={theme} forceColorScheme={scheme}>
      {children}
    </MantineProvider>
  );
}

// Keep the existing business handlers and native button semantics while sharing library controls.
export function Button({
  className = "",
  type = "button",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  const classes = className.split(/\s+/);
  if (classes.includes("button")) {
    const variant = classes.includes("primary")
      ? "filled"
      : classes.includes("ghost")
        ? "subtle"
        : "default";
    return (
      <MantineButton
        {...props}
        type={type}
        variant={variant}
        className={[
          "ui-action",
          ...classes.filter(
            (name) =>
              !["button", "primary", "secondary", "ghost"].includes(name),
          ),
        ].join(" ")}
      />
    );
  }
  if (classes.includes("icon-button"))
    return (
      <ActionIcon
        {...props}
        type={type}
        className={`ui-icon ${classes.filter((name) => name !== "icon-button").join(" ")}`}
      />
    );
  return <UnstyledButton {...props} type={type} className={className} />;
}

export function Sheet({
  title,
  onClose,
  children,
  side = false,
  wide = false,
  compact = false,
  trapFocus = true,
  closeOnEscape = true,
  zIndex = 400,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  side?: boolean;
  wide?: boolean;
  compact?: boolean;
  trapFocus?: boolean;
  closeOnEscape?: boolean;
  zIndex?: number;
}) {
  const mobile = useMediaQuery("(max-width: 700px)", false, {
    getInitialValueInEffect: false,
  });
  const reducedMotion = useReducedMotion();
  const common = {
    opened: true,
    onClose,
    title,
    zIndex,
    closeButtonProps: { "aria-label": "关闭弹窗", size: "lg" },
    overlayProps: { backgroundOpacity: 0.38, blur: 3 },
    transitionProps: { duration: reducedMotion ? 0 : 180 },
    trapFocus,
    returnFocus: true,
    closeOnEscape,
  };
  if (side || mobile)
    return (
      <Drawer
        {...common}
        position={mobile ? "bottom" : "right"}
        size={mobile ? (side ? "100%" : "auto") : wide ? 820 : 560}
        radius={mobile && !side ? "xl" : 0}
        classNames={{
          content: `ui-sheet ${side ? "ui-sheet-editor" : ""} ${compact && !side ? "ui-sheet-compact" : ""}`,
          header: "ui-sheet-header",
          body: "ui-sheet-body",
          title: "ui-sheet-title",
        }}
      >
        {children}
      </Drawer>
    );
  return (
    <Modal
      {...common}
      centered
      size={wide ? 960 : 600}
      padding={0}
      radius="xl"
      classNames={{
        content: "ui-modal",
        header: "ui-sheet-header",
        body: "ui-modal-body",
        title: "ui-sheet-title",
      }}
    >
      {children}
    </Modal>
  );
}
